import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Form, FormInstance, Input, Upload } from 'antd';
import { openErrorNoti } from 'presentation/components/Notification';
import { useAppSelector } from 'presentation/redux/hooks';
import React from 'react';
import getMessage from 'utilities/constants/MESSAGE';
import { PLACEHOLDER } from 'utilities/constants/PLACEHOLDER';
import { commonHelpers } from 'utilities/helpers';
import { ENV } from '../../../../../infrastructure/config/CONSTANTS';

interface Props {
    form: FormInstance;
    onSubmit: (values: any) => void;
    sendFileMessage: (fileUrl: string, file: File, data: any) => void;
}

const ChatForm = ({ form, onSubmit, sendFileMessage }: Props) => {
    const [message, setMessage] = React.useState<string>();
    const chatCounterSlice = useAppSelector((state) => state.chatCounterSlice);
    const fileTypes = ENV.FILE_TYPE_CU_012_01?.split(',');
    const { TextArea } = Input;
    const fileUploadProps = {
        showUploadList: false,
        multiple: true,
        accept: fileTypes?.join(','),
        customRequest: async (componentsData: any) => {
            const file: File = componentsData.file;
            const maxSizeMB = ENV.FILE_MAX_SIZE_CU_012_01;
            const invalidFileType = commonHelpers.validateFileTypes(file, fileTypes);
            if (invalidFileType) {
                openErrorNoti({ description: getMessage('M007', invalidFileType) });
                return;
            }
            const isValidFileSize = commonHelpers.validateFileSize(file, maxSizeMB);
            if (!isValidFileSize) {
                openErrorNoti({ description: getMessage('M006', maxSizeMB + 'MB') });
                return;
            }

            const chatRoomId = chatCounterSlice.chatRoom?.chatRoomId;
            if (!chatRoomId) return;

            const data = {
                file,
                chatRoomId: chatRoomId,
            };
            await getBase64(file, async (url) => {
                await sendFileMessage(url, file, data);
            });
        },
        beforeUpload: (file: File, fileList: File[]) => {
            const limitDrop = Number(ENV.FILE_MAX_NUMBER_CU_012_01);
            if (fileList && limitDrop && fileList.length > limitDrop) {
                const message = getMessage('M010', limitDrop);
                openErrorNoti({ description: message });
                return false;
            }

            return true;
        },
    };

    const getBase64 = (file: File, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(file);
    };

    return (
        <div className="chat-section">
            <Form form={form} onFinish={onSubmit} className="chat-form">
                <Upload {...fileUploadProps}>
                    <Button
                        type="link"
                        size="large"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        className="add-button"
                    >
                        <PlusCircleOutlined style={{ fontSize: 24, color: 'var(--primary)' }} />
                    </Button>
                </Upload>
                <Form.Item name={'message'} className="input-chat-form">
                    <TextArea
                        autoSize
                        placeholder={PLACEHOLDER.CHAT_MESSAGE}
                        size="large"
                        autoFocus
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={1000}
                        onKeyDown={(e) => {
                            // Check if user starts entering a Japan character using a Pinyin or IME
                            // refer to https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/isComposing
                            // and https://developer.mozilla.org/en-US/docs/Web/API/Element/compositionstart_event
                            if (e.key.toLowerCase() == 'enter' && !e.nativeEvent.isComposing && !e.shiftKey) {
                                e.preventDefault();
                                form.submit();
                            }
                        }}
                    />
                </Form.Item>
                <Button
                    type="primary"
                    size="large"
                    className="chat-submit-button"
                    htmlType="submit"
                    disabled={!message || message === ''}
                >
                    送信
                </Button>
            </Form>
        </div>
    );
};

export default ChatForm;
